import React, { useCallback, useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { listMissionFieldReports } from '../../services/missionApi';
import { SubjectCard, createSubjectCard, listSubjectCards } from '../../services/subjectCardApi';
import { formatWhen } from '../missions/format';
import { SubjectDossierDialog, SubjectScope } from './SubjectDossier';

const GOLD = '#f5b942';

const threatColor: Record<string, string> = {
  clear: '#66bb6a',
  watch: GOLD,
  elevated: '#ef9a36',
  high: '#e57373',
  critical: '#ef5350',
};

function photoCapturePoint(cards: SubjectCard[]): { longitude: number; latitude: number } | null {
  const points: Array<{ longitude: number; latitude: number; at: number }> = [];
  for (const card of cards) {
    for (const sighting of card.sightings || []) {
      if (!Number.isFinite(sighting.latitude) || !Number.isFinite(sighting.longitude)) continue;
      points.push({
        longitude: sighting.longitude as number,
        latitude: sighting.latitude as number,
        at: sighting.observed_at ? Date.parse(sighting.observed_at) : 0,
      });
    }
    for (const loc of card.derived?.locations || []) {
      if (!Number.isFinite(loc.latitude) || !Number.isFinite(loc.longitude)) continue;
      points.push({
        longitude: loc.longitude,
        latitude: loc.latitude,
        at: loc.observed_at ? Date.parse(loc.observed_at) : 0,
      });
    }
  }
  if (!points.length) return null;
  points.sort((a, b) => b.at - a.at);
  return { longitude: points[0].longitude, latitude: points[0].latitude };
}

export interface SubjectDossierPanelProps {
  missionId?: number;
  packetId?: number;
  onError: (message: string | null) => void;
  refreshMs?: number;
  onFocus?: (longitude: number, latitude: number) => void;
  /** Open the dock Photos tab. */
  onOpenPhotos?: () => void;
}

/**
 * Named people opened from a face match, or added by the analyst.
 * Compact list in the mission/packet panel; the baseball card opens on click.
 */
const SubjectDossierPanel: React.FC<SubjectDossierPanelProps> = ({
  missionId,
  packetId,
  onError,
  refreshMs = 15000,
  onFocus,
  onOpenPhotos,
}) => {
  const [cards, setCards] = useState<SubjectCard[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [alias, setAlias] = useState('');
  const scope: SubjectScope | null = missionId
    ? { missionId }
    : packetId
      ? { packetId }
      : null;

  const load = useCallback(() => {
    if (!scope) return;
    listSubjectCards(scope)
      .then(setCards)
      .catch(() => setCards([]));
  }, [missionId, packetId]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, refreshMs);
    return () => window.clearInterval(timer);
  }, [load, refreshMs]);

  if (!scope) return null;
  const selected = cards.find((c) => c.id === openId) || null;
  const scopeKey = missionId ? `m-${missionId}` : `p-${packetId}`;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>Subjects</Typography>
        <Typography
          variant="caption"
          onClick={() => setAdding((v) => !v)}
          sx={{
            fontWeight: 700,
            cursor: 'pointer',
            color: '#c6cdd5',
            border: '1px solid #333a45',
            borderRadius: 0.75,
            px: 0.7,
            '&:hover': { color: '#e8ebee' },
          }}
        >
          {adding ? 'Cancel' : 'Add'}
        </Typography>
      </Box>
      {adding && (
        <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
          <TextField
            size="small"
            placeholder="Alias or name"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
          />
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: GOLD, alignSelf: 'center', cursor: 'pointer' }}
            onClick={() => {
              const name = alias.trim();
              if (!name) return;
              createSubjectCard(scope, { alias: name })
                .then((card) => {
                  setCards((rows) => [card, ...rows.filter((r) => r.id !== card.id)]);
                  setAlias('');
                  setAdding(false);
                  setOpenId(card.id);
                })
                .catch((err) => onError(err instanceof Error ? err.message : 'Could not add subject'));
            }}
          >
            Create
          </Typography>
        </Box>
      )}
      {cards.map((card) => (
        <Box
          key={`${scopeKey}-${card.id}`}
          onClick={() => setOpenId(card.id)}
          sx={{
            py: 0.55,
            cursor: 'pointer',
            '&:hover .name': { color: GOLD },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography className="name" variant="caption" sx={{ fontWeight: 700, flex: 1 }} noWrap>
              {card.alias}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: threatColor[card.threat_level] || GOLD, fontWeight: 700, fontSize: '0.62rem' }}
            >
              {(card.threat_level || 'watch').toUpperCase()}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
            {card.sighting_count} sighting{card.sighting_count === 1 ? '' : 's'}
            {card.last_seen ? ` · ${formatWhen(card.last_seen)}` : ''}
            {card.packet_id && 'missionId' in scope ? ' · in packet' : ''}
          </Typography>
        </Box>
      ))}
      {onOpenPhotos && (
        <Typography
          variant="caption"
          onClick={() => {
            const go = (at: { longitude: number; latitude: number } | null) => {
              if (at) onFocus?.(at.longitude, at.latitude);
              onOpenPhotos();
            };
            const known = photoCapturePoint(cards);
            if (known || !missionId) {
              go(known);
              return;
            }
            listMissionFieldReports(missionId)
              .then((rows) => {
                const photo = rows.find((row) => (
                  Number.isFinite(row.latitude) && Number.isFinite(row.longitude)
                ));
                go(photo
                  ? { longitude: photo.longitude, latitude: photo.latitude }
                  : null);
              })
              .catch(() => go(null));
          }}
          sx={{
            display: 'inline-block',
            mt: 0.75,
            fontWeight: 700,
            cursor: 'pointer',
            color: GOLD,
            border: '1px solid #3d3319',
            borderRadius: 0.75,
            px: 0.85,
            py: 0.25,
            '&:hover': { bgcolor: 'rgba(245,185,66,0.12)' },
          }}
        >
          Photos
        </Typography>
      )}
      <SubjectDossierDialog
        open={!!selected}
        card={selected}
        scope={scope}
        onClose={() => setOpenId(null)}
        onSaved={(next) => {
          setCards((rows) => rows.map((r) => (r.id === next.id ? next : r)));
        }}
        onError={onError}
      />
    </Box>
  );
};

export default SubjectDossierPanel;
